import { useLoadScript } from '@react-google-maps/api';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { googleMapsApiKey } from '~/constants/environment';
import type { VendingMachineLocation } from '~/contracts/vm';
import CustomGoogleMap from './CustomGoogleMap';
import { geographicCenterOfUnitedStates } from './LocationData';
import { MapInnerContainer, MapOuterContainer } from './MapFinder.styles';
import { MapFinderSearchArea } from './MapFinderSearchArea';
import { MapLegend } from './MapLegend';
import IntersectionObserverWrapper from '../Shared/IntersectionObserverWrapper';

const zoomedOutStyles: google.maps.MapTypeStyle[] = [
  {
    elementType: 'labels.text',
    featureType: 'administrative.locality',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        color: '#BED6ED'
      }
    ]
  }
];

const zoomedInStyles: google.maps.MapTypeStyle[] = [
  {
    elementType: 'labels.text',
    featureType: 'administrative.locality',
    stylers: [
      {
        color: 'yellow',
        weight: 0
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        color: '#BED6ED'
      }
    ]
  }
];

type OwnProps = {
  currentZip: string;
  defaultLocation?: { lat: number; lng: number };
  hasLegend?: boolean;
  isNationwide?: boolean;
  onSubmit: () => void;
  onZipChange: (value: string) => void;
  setGeocoder: (geocoder?: google.maps.Geocoder) => void;
  setGeoCoords: ({
    lat,
    lng,
    label
  }: {
    lat: number;
    lng: number;
    label: string;
  }) => void;
  vendingMachineLocations: VendingMachineLocation[];
};

export default function MapFinder({
  currentZip = '',
  defaultLocation = geographicCenterOfUnitedStates,
  hasLegend = true,
  isNationwide = true,
  onSubmit,
  onZipChange,
  setGeocoder,
  setGeoCoords,
  vendingMachineLocations
}: OwnProps) {
  const [activeLocation, updateActiveLocation] = useState<{ label: string }>({
    label: ''
  });
  const [currentZoomStyles, updateZoomStyles] =
    useState<google.maps.MapTypeStyle[]>(zoomedOutStyles);
  const [showMarkerLabels, updateShowMarkerLabels] = useState<boolean>(false);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  let center: google.maps.LatLngLiteral;
  let zoom = 10;

  if (isNationwide) {
    center = geographicCenterOfUnitedStates;
    zoom = 4.7;
  } else {
    center = defaultLocation;
    zoom = 15;
    // find center by city/state
  }

  const { isLoaded } = useLoadScript({ googleMapsApiKey });

  const handleZoomChange = useCallback(() => {
    if (!map) return;
    const currentZoom = map.getZoom() ?? 0;
    updateZoomStyles(currentZoom > 5 ? zoomedInStyles : zoomedOutStyles);
    updateShowMarkerLabels(currentZoom > 5);
  }, [map, updateZoomStyles, updateShowMarkerLabels]);

  const useOnLoad = useCallback(
    (mapResult: google.maps.Map) => {
      mapResult.setCenter(center);

      if (!isNationwide) {
        const opt = { minZoom: zoom - 4, maxZoom: zoom + 4 };
        updateShowMarkerLabels(true);
        mapResult.setOptions(opt);
      }

      setMap(mapResult);
    },
    [center, isNationwide, zoom]
  );

  const handleMarkerClick = useCallback(
    (label: string, event: google.maps.MapMouseEvent) => {
      if (!map || !event.latLng) return;

      const location = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
        label
      };

      updateActiveLocation(location);
      setGeoCoords(location);

      const currentZoom = map.getZoom() ?? 0;

      // Use requestAnimationFrame instead of setTimeout for smoother animation
      requestAnimationFrame(() => {
        map.panTo(location);
        if (currentZoom < 8) {
          map.setZoom(8);
        }
      });
    },
    [map, setGeoCoords]
  );

  let maybeMap: ReactElement;

  useEffect(() => {
    if (activeLocation.label) {
      const frame = requestAnimationFrame(() => {
        updateActiveLocation({ label: '' });
      });

      return () => cancelAnimationFrame(frame);
    }
  }, [activeLocation.label]);

  useEffect(() => {
    if (isLoaded) {
      const geocoder = new google.maps.Geocoder();
      setGeocoder(geocoder);
    }
  }, [isLoaded, setGeocoder, setMap]);

  if (isLoaded) {
    maybeMap = (
      <IntersectionObserverWrapper>
        {(isVisible: boolean) => (
          <CustomGoogleMap
            activeLocation={activeLocation}
            center={center}
            enableClustering={false}
            isVisible={isVisible}
            onMapLoad={useOnLoad}
            onMarkerClick={handleMarkerClick}
            onZoomChanged={handleZoomChange}
            showMarkerLabels={showMarkerLabels}
            vendingMachineLocations={vendingMachineLocations}
            zoom={zoom}
            zoomStyles={currentZoomStyles}
          />
        )}
      </IntersectionObserverWrapper>
    );
  } else {
    maybeMap = <div />;
  }
  maybeMap = (
    <MapOuterContainer>
      <MapInnerContainer>
        {maybeMap}
        {hasLegend ? (
          <>
            <MapFinderSearchArea
              onChange={searchTerm => {
                onZipChange(searchTerm);
              }}
              onSubmit={() => onSubmit()}
              searchTerm={currentZip}
            />
            {map && (
              <MapLegend
                activeLocation={activeLocation}
                map={map}
                updateActiveLocation={updateActiveLocation}
                vendingMachineLocations={vendingMachineLocations}
              />
            )}
          </>
        ) : null}
      </MapInnerContainer>
    </MapOuterContainer>
  );

  return maybeMap;
}
